"use client";

import { useTranslations } from "next-intl";
import { StudioLiquidBg } from "@/components/scene-bg/StudioLiquidBg";
import { CornerFrame, ScrollReveal } from "../scroll-reveal";
import { CountUp } from "../count-up";

export function Studio() {
  const t = useTranslations("studio");

  return (
    <section className="relative py-12 sm:py-20 lg:py-28">
      <StudioLiquidBg />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <ScrollReveal className="flex flex-col gap-5 lg:col-span-5">
            <div className="flex items-center justify-between">
              <span data-blend className="inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.18em]">
                <span aria-hidden className="size-1 rounded-full bg-aurora-cyan" />
                {t("eyebrow")}
              </span>
              <span data-blend className="font-mono text-[10px] uppercase tracking-[0.18em] opacity-60 hidden sm:inline">
                / kessem.studio
              </span>
            </div>
            <h2 data-blend className="font-sans text-3xl font-medium leading-[1.05] tracking-[-0.03em] sm:text-5xl md:text-6xl">
              {t("title")}
              <br />
              <span data-blend className="font-display italic">{t("subtitle")}</span>
            </h2>
          </ScrollReveal>

          <ScrollReveal as="div" className="flex flex-col gap-8 lg:col-span-7" delay={0.1}>
            <p data-blend className="font-display text-xl leading-snug sm:text-2xl md:text-3xl">
              {t("body")}
            </p>

            <CornerFrame className="grid grid-cols-3 gap-4 border-t border-border/60 pt-8">
              <Metric
                value={t("metric1Value")}
                label={t("metric1Label")}
              />
              <Metric
                value={t("metric2Value")}
                label={t("metric2Label")}
                divided
              />
              <Metric
                value={t("metric3Value")}
                label={t("metric3Label")}
                divided
              />
            </CornerFrame>

            <span data-blend className="font-mono text-[11px] uppercase tracking-[0.18em]">
              {t("tag")}
            </span>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}

function Metric({
  value,
  label,
  divided = false,
}: {
  value: string;
  label: string;
  divided?: boolean;
}) {
  return (
    <div
      className={`flex flex-col gap-1 ${
        divided ? "border-s border-border/60 ps-4" : ""
      }`}
    >
      <span data-blend className="font-display text-3xl tracking-tight sm:text-4xl">
        <CountUp value={value} />
      </span>
      <span data-blend className="text-[10px] font-medium uppercase tracking-[0.16em] sm:text-[11px]">
        {label}
      </span>
    </div>
  );
}
